import React, { createContext, useContext, useReducer, useRef } from 'react';
import { CheckCircle, AlertCircle, AlertTriangle, Info, X } from 'lucide-react';

// Toast context
const ToastContext = createContext();

// Toast types
export const TOAST_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
};

// Action types
const ActionTypes = {
  ADD_TOAST: 'ADD_TOAST',
  REMOVE_TOAST: 'REMOVE_TOAST',
  CLEAR_ALL: 'CLEAR_ALL',
};

// Reducer
const toastReducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.ADD_TOAST:
      return [...state, action.payload];
    case ActionTypes.REMOVE_TOAST:
      return state.filter(toast => toast.id !== action.payload);
    case ActionTypes.CLEAR_ALL:
      return [];
    default:
      return state;
  }
};

// Toast provider
export const ToastProvider = ({ children, maxToasts = 5 }) => {
  const [toasts, dispatch] = useReducer(toastReducer, []);
  const idRef = useRef(0);

  const addToast = (message, type = TOAST_TYPES.INFO, options = {}) => {
    const id = ++idRef.current;
    const toast = {
      id,
      message,
      type,
      duration: options.duration ?? 5000,
      persistent: options.persistent ?? false,
      action: options.action,
      ...options,
    };

    dispatch({ type: ActionTypes.ADD_TOAST, payload: toast });

    // Auto remove if not persistent
    if (!toast.persistent && toast.duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, toast.duration);
    }

    // Remove oldest toast if max limit exceeded
    if (toasts.length >= maxToasts) {
      dispatch({ type: ActionTypes.REMOVE_TOAST, payload: toasts[0].id });
    }

    return id;
  };

  const removeToast = (id) => {
    dispatch({ type: ActionTypes.REMOVE_TOAST, payload: id });
  };

  const clearAll = () => {
    dispatch({ type: ActionTypes.CLEAR_ALL });
  };

  const success = (message, options) => addToast(message, TOAST_TYPES.SUCCESS, options);
  const error = (message, options) => addToast(message, TOAST_TYPES.ERROR, options);
  const warning = (message, options) => addToast(message, TOAST_TYPES.WARNING, options);
  const info = (message, options) => addToast(message, TOAST_TYPES.INFO, options);

  const value = {
    toasts,
    addToast,
    removeToast,
    clearAll,
    success,
    error,
    warning,
    info,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer />
    </ToastContext.Provider>
  );
};

// Toast hook
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// Individual toast component
const Toast = ({ toast, onRemove }) => {
  const { id, message, type, action } = toast;

  const icons = {
    [TOAST_TYPES.SUCCESS]: CheckCircle,
    [TOAST_TYPES.ERROR]: AlertCircle,
    [TOAST_TYPES.WARNING]: AlertTriangle,
    [TOAST_TYPES.INFO]: Info,
  };

  const styles = {
    [TOAST_TYPES.SUCCESS]: 'bg-green-50 border-green-200 text-green-800',
    [TOAST_TYPES.ERROR]: 'bg-red-50 border-red-200 text-red-800',
    [TOAST_TYPES.WARNING]: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    [TOAST_TYPES.INFO]: 'bg-blue-50 border-blue-200 text-blue-800',
  };

  const iconStyles = {
    [TOAST_TYPES.SUCCESS]: 'text-green-400',
    [TOAST_TYPES.ERROR]: 'text-red-400',
    [TOAST_TYPES.WARNING]: 'text-yellow-400',
    [TOAST_TYPES.INFO]: 'text-blue-400',
  };

  const Icon = icons[type];

  return (
    <div
      className={`
        flex items-start p-4 rounded-lg border shadow-sm transition-all duration-300 ease-in-out
        transform translate-x-0 opacity-100 max-w-sm w-full
        ${styles[type]}
      `}
      role="alert"
    >
      <div className="flex-shrink-0">
        <Icon className={`w-5 h-5 ${iconStyles[type]}`} />
      </div>

      <div className="ml-3 flex-1">
        <div className="text-sm font-medium">
          {message}
        </div>

        {action && (
          <div className="mt-2">
            <button
              type="button"
              className="text-sm underline hover:no-underline focus:outline-none"
              onClick={action.onClick}
            >
              {action.label}
            </button>
          </div>
        )}
      </div>

      <div className="ml-4 flex-shrink-0">
        <button
          type="button"
          className="inline-flex text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600 transition-colors duration-200"
          onClick={() => onRemove(id)}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// Toast container
const ToastContainer = () => {
  const { toasts, removeToast } = useToast();

  return (
    // <div
    //   className="fixed top-4 right-4 z-50 space-y-2"
    //   aria-live="polite"
    //   aria-label="Notifications"
    // >
    <div
  className="fixed top-4 right-4 z-[9999] space-y-2"
  aria-live="polite"
  aria-label="Notifications"
>

      {toasts.map(toast => (
        <Toast
          key={toast.id}
          toast={toast}
          onRemove={removeToast}
        />
      ))}
    </div>
  );
};

export default Toast;